import styled from '@emotion/styled'
import Theme, { StyledHTMLElement } from 'shop/theme/types'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import { useHorizontalDragScroll, useResize } from 'shop/hooks'
import { disableBlueTapHighlight } from 'shop/components/common'
import {
  IoChevronBack as LeftChevron,
  IoChevronForward as RightChevron
} from 'react-icons/io5'

type Props = {
  days: Date[]
  tempSelectedFulfillmentDate: Date
  onSelect: (day: Date) => void
}

const DaysSelectorList = ({
  days,
  tempSelectedFulfillmentDate,
  onSelect
}: Props) => {
  const scrollContainerRef = useRef<HTMLUListElement>(null)
  const selectedDayRef = useRef<HTMLLIElement>(null)
  const containerSize = useResize(scrollContainerRef)
  const [showLeftScrollButton, setShowLeftScrollButton] = useState(false)
  const [showRightScrollButton, setShowRightScrollButton] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const { onMouseDown, onMouseMove, onMouseUp } =
    useHorizontalDragScroll(scrollContainerRef)

  const onScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      let containerWidth = 0

      if (containerSize?.width && containerSize?.x) {
        containerWidth = containerSize?.width + containerSize?.x * 2
      }
      const isScrollable =
        (scrollContainerRef.current?.scrollWidth || 0) > containerWidth

      setShowLeftScrollButton(isScrollable && scrollLeft > 0)
      setShowRightScrollButton(
        isScrollable && Math.round(scrollLeft + clientWidth) < scrollWidth
      )
    }
  }, [containerSize])

  const handleWheelScroll = (event: React.WheelEvent) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += event.deltaY
    }
  }

  const checkIsSelected = (day: Date, selectedDay: Date) => {
    if (format(day, 'dd/MM/yy') === format(selectedDay, 'dd/MM/yy')) {
      return true
    }
    return false
  }

  // Scroll the selected DayItem into view, on load of component and whenever a new Day is selected
  useLayoutEffect(() => {
    if (selectedDayRef.current && scrollContainerRef.current) {
      const selectedDayLeftPos =
        selectedDayRef.current.offsetLeft -
        selectedDayRef.current?.clientWidth * 4
      scrollContainerRef.current.scrollTo({
        left: selectedDayLeftPos,
        behavior: 'smooth'
      })
      onScroll()
      setHasLoaded(true)
    }

    if (hasLoaded) {
      selectedDayRef.current?.scrollIntoView({
        behavior: 'smooth'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSelectedFulfillmentDate])

  const handleScrollButtonClick = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const shiftAmount = scrollContainerRef.current.scrollWidth / 3
      if (direction === 'left') {
        scrollContainerRef.current.scrollBy({
          left: -shiftAmount,
          behavior: 'smooth'
        })
      }
      if (direction === 'right') {
        scrollContainerRef.current.scrollBy({
          left: shiftAmount,
          behavior: 'smooth'
        })
      }
    }
  }

  return (
    <DayListContainer>
      {showLeftScrollButton && (
        <>
          <ScrollIndicatorIcon
            side='left'
            onClick={() => handleScrollButtonClick('left')}
          >
            <LeftChevron />
          </ScrollIndicatorIcon>
          <CircleBackground side='left' />
        </>
      )}
      <DayItemContainer
        ref={scrollContainerRef}
        onWheel={handleWheelScroll}
        onScroll={onScroll}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {days.map((day) => {
          const isSelected = checkIsSelected(day, tempSelectedFulfillmentDate)

          return (
            <DayItem
              ref={isSelected ? selectedDayRef : null}
              key={format(day, 'dd/MM/yy')}
              onClick={() => onSelect(day)}
              isSelected={isSelected}
            >
              <span>{format(day, 'EEE')}</span>
              <span>{format(day, 'dd')}</span>
            </DayItem>
          )
        })}
      </DayItemContainer>
      {showRightScrollButton && (
        <>
          <ScrollIndicatorIcon
            side='right'
            onClick={() => handleScrollButtonClick('right')}
          >
            <RightChevron />
          </ScrollIndicatorIcon>
          <CircleBackground side='right' />
        </>
      )}
    </DayListContainer>
  )
}

const DayListContainer = styled.div<StyledHTMLElement, Required<Theme>>(
  ({ theme }) => ({
    position: 'relative',

    [theme.mediaQueries.viewport7]: {
      margin: '0 20px' // margin for ScrollIndicatorIcon
    }
  })
)

const DayItemContainer = styled.ul<StyledHTMLElement, Required<Theme>>(
  ({ theme }) => ({
    display: 'flex',
    position: 'relative',
    gap: '8px',
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    margin: 0
  })
)
const DayItem = styled.li<
  StyledHTMLElement & { isSelected: boolean },
  Required<Theme>
>(({ theme, isSelected }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '62px',
  minWidth: '56px',
  backgroundColor: isSelected ? theme.colors['primary'] : '#F5F5F5',
  borderRadius: '12px',
  cursor: 'pointer',
  color: isSelected ? '#fff' : '#000',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: theme.fonts['body'].family,

  '&:hover': {
    opacity: 0.8
  },
  '&:active': {
    opacity: 0.6
  },

  ...disableBlueTapHighlight(theme)
}))

const ScrollIndicatorIcon = styled.div<
  StyledHTMLElement & { side: 'left' | 'right' },
  Required<Theme>
>(({ theme, side }) => ({
  display: 'none',
  alignContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '14px',
  padding: '6px 6px',
  borderRadius: '32px',
  border: '1px solid #fff',
  backgroundColor: theme.colors['primary'],
  zIndex: theme.zIndex.addressSelectionModal + 2,
  cursor: 'pointer',

  ...(side === 'left' && {
    left: 0,
    marginLeft: '-16px'
  }),
  ...(side === 'right' && {
    right: 0,
    marginRight: '-16px'
  }),

  '& svg': {
    height: '20px',
    width: '20px',
    color: '#fff'
  },

  ':hover': {
    opacity: 0.8
  },

  '&:active': {
    opacity: 0.6
  },

  [theme.mediaQueries.viewport7]: {
    display: 'flex'
  },

  ...disableBlueTapHighlight(theme)
}))

/** A plain white circle to allow the ScrollIndicatorIcon to remain consistent across the site
 *  using the :hover and :active states opacity logic. */
const CircleBackground = styled.div<
  StyledHTMLElement & { side: 'left' | 'right' },
  Required<Theme>
>(({ theme, side }) => ({
  display: 'none',
  position: 'absolute',
  height: '34px',
  width: '34px',
  borderRadius: '36px',
  backgroundColor: '#fff',
  zIndex: 301,

  top: '14px',
  ...(side === 'left' && {
    left: 0,
    marginLeft: '-16px'
  }),
  ...(side === 'right' && {
    right: 0,
    marginRight: '-16px'
  }),

  [theme.mediaQueries.viewport7]: {
    display: 'block'
  }
}))
export default DaysSelectorList
